import { useState, useEffect, useRef, Fragment } from 'react';
import { Autocomplete, CircularProgress, Popper, TextField } from '@mui/material';
import { PopperProps } from '@mui/material/Popper';

export interface ILazyDropdownOption<T> {
    value: T;
    label: string;
}

interface ILazyDropdownProps<T> {
    value: ILazyDropdownOption<T> | null;
    onChange: (option: ILazyDropdownOption<T> | null) => void;
    fetchOptions: (term: string) => Promise<ILazyDropdownOption<T>[]>;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    debounceMs?: number;
    minChars?: number;
}

function LazyPopper(props: PopperProps) {
    return <Popper {...props} style={{ ...props.style, zIndex: 1000000 }} />;
}

export function LazyDropdown<T,>({
    value,
    onChange,
    fetchOptions,
    label,
    placeholder,
    disabled,
    debounceMs = 300,
    minChars = 3,
}: ILazyDropdownProps<T>) {
    const [inputValue, setInputValue] = useState(value?.label ?? '');
    const [options, setOptions] = useState<ILazyDropdownOption<T>[]>([]);
    const [loading, setLoading] = useState(false);
    const fetchRef = useRef(fetchOptions);
    fetchRef.current = fetchOptions;

    useEffect(() => {
        setInputValue(value?.label ?? '');
    }, [value]);

    useEffect(() => {
        if (inputValue.length < minChars) {
            setOptions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const result = await fetchRef.current(inputValue);
                setOptions(result);
            } finally {
                setLoading(false);
            }
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [inputValue, debounceMs, minChars]);

    return (
        <Autocomplete
            value={value}
            inputValue={inputValue}
            options={options}
            loading={loading}
            disabled={disabled}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            onInputChange={(_, newInput, reason) => {
                if (reason === 'reset') {
                    return;
                }
                setInputValue(newInput);
            }}
            onChange={(_, newValue) => onChange(newValue)}
            PopperComponent={LazyPopper}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <Fragment>
                                {loading && <CircularProgress size={16} />}
                                {params.InputProps.endAdornment}
                            </Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}
